'use client'

import { cn, hashString } from '@/lib/utils'
import { CurlyBraces, FileIcon, XIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { ComponentProps, useEffect, useMemo, useRef, useState } from 'react'
import { SiJavascript, SiReact, SiTypescript } from 'react-icons/si'
import SyntaxHighlighter from 'react-syntax-highlighter'
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import { ScrollArea } from '../ui/scroll-area'

export type Tab = {
  filename: string
  content: string
}

export type CodeEditorProps = {
  tabs: Array<Tab>
  defaultTab?: string
  closeTab?: (tab: Tab) => void
  editorMaxHeight?: number
} & ComponentProps<'div'>

function Tab({
  tab,
  currentTab,
  setCurrentTab,
  closeTab,
}: {
  tab: Tab
  currentTab?: Tab
  setCurrentTab: (tab: Tab) => void
  closeTab: (tab: Tab) => void
}) {
  const Icon = useMemo(() => {
    const extension = tab.filename.split('.').pop()
    switch (extension) {
      case 'tsx':
      case 'jsx':
        return SiReact
      case 'ts':
        return SiTypescript
      case 'js':
        return SiJavascript
      case 'json':
        return CurlyBraces
      default:
        return FileIcon
    }
  }, [tab.filename])

  return (
    <div
      className={cn({
        'text-muted-foreground hover:bg-muted-background hover:text-primary-background flex cursor-pointer items-center justify-center gap-2 px-4 py-3 text-sm transition-colors first:rounded-tl-lg':
          true,
        'bg-accent text-accent-foreground hover:bg-accent': tab.filename === currentTab?.filename,
      })}
      onClick={() => setCurrentTab(tab)}
    >
      <Icon className="text-muted-foreground h-4 w-4" />
      {tab.filename}
      {closeTab && (
        <XIcon
          onClick={(e) => {
            e.stopPropagation()
            closeTab(tab)
          }}
          size={10}
          className="text-muted-foreground"
        />
      )}
    </div>
  )
}

const highlighterStyle = {
  ...atomOneDark,
  hljs: {
    background: 'var(--primary-foreground)',
  },
}

export function CodeEditor({
  tabs,
  defaultTab,
  closeTab,
  className,
  editorMaxHeight,
  ...props
}: CodeEditorProps) {
  const [currentTab, setCurrentTab] = useState<Tab | undefined>(
    tabs.find((t) => t.filename === defaultTab) ?? tabs.at(0),
  )

  useEffect(() => {
    if (!currentTab) {
      setCurrentTab(tabs.at(0))
    }
  }, [tabs])

  // Keep track of the previous tabs array to detect new tabs
  const prevTabsRef = useRef<Tab[]>(tabs)

  useEffect(() => {
    // If the current tab is no longer in the tabs array, select the last tab
    if (currentTab && !tabs.some((tab) => tab.filename === currentTab.filename)) {
      setCurrentTab(tabs.at(-1))
      return
    }

    // If a new tab is added, set it as the current tab
    if (tabs.length > prevTabsRef.current.length) {
      // Find the new tab (the one that wasn't in the previous tabs array)
      const newTab = tabs.find(
        (tab) => !prevTabsRef.current.some((prevTab) => prevTab.filename === tab.filename),
      )
      if (newTab) {
        setCurrentTab(newTab)
      }
    }

    // Update current tab if its content has changed
    if (currentTab) {
      const updatedTab = tabs.find((tab) => tab.filename === currentTab.filename)
      if (updatedTab && updatedTab.content !== currentTab.content) {
        setCurrentTab(updatedTab)
      }
    }

    // Update the reference to the current tabs array
    prevTabsRef.current = [...tabs]
  }, [tabs, currentTab])

  return (
    <div
      className={cn('bg-sidebar border-border flex flex-col rounded-lg border', className)}
      {...props}
    >
      {tabs && tabs.length > 0 && (
        <div className="divide-border border-border flex items-center justify-start divide-x border-b">
          {tabs.map((tab, index) => (
            <Tab
              key={tab.filename}
              tab={tab}
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
              closeTab={(closedTab) => {
                setCurrentTab(tabs.at(index - 1))
                closeTab(closedTab)
              }}
            />
          ))}
        </div>
      )}
      <AnimatePresence mode={'wait'}>
        {currentTab && (
          <motion.div
            key={`${currentTab.filename}-${hashString(currentTab.content)}`}
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            exit={{ opacity: 0 }}
            className="max-w-full grow p-2"
          >
            <ScrollArea
              className="w-full"
              style={{
                height: editorMaxHeight ? `${editorMaxHeight}px` : '100%',
              }}
            >
              <SyntaxHighlighter
                language="typescript"
                style={highlighterStyle}
                showLineNumbers={true}
                codeTagProps={{
                  style: {
                    whiteSpace: 'pre-wrap',
                  },
                }}
              >
                {currentTab.content.trim()}
              </SyntaxHighlighter>
            </ScrollArea>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
